import React, { useEffect, useState } from 'react';
import { Alert, Button, Space, Table, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { AdminTag } from '../../types/tag.types';
import { tagService } from '../../services/tag.service';
import { TagFormModal, TagRowActions, TagStatusBadge } from '../../components/admin/tags/TagFormModal';

export const AdminTagsPage: React.FC = () => {
  const [items, setItems] = useState<AdminTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<AdminTag | null>(null);

  const loadTags = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tagService.listAdminTags();
      setItems(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erro ao carregar etiquetas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  const openCreate = () => {
    setEditingTag(null);
    setFormOpen(true);
  };

  const openEdit = (tag: AdminTag) => {
    setEditingTag(tag);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingTag(null);
  };

  const handleSave = async (values: { name: string }) => {
    try {
      setSaving(true);

      if (editingTag) {
        await tagService.updateAdminTag(editingTag.id, { name: values.name });
        message.success('Etiqueta atualizada com sucesso');
      } else {
        await tagService.createAdminTag({ name: values.name });
        message.success('Etiqueta criada com sucesso');
      }

      closeForm();
      await loadTags();
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Nao foi possivel salvar a etiqueta');
    } finally {
      setSaving(false);
    }
  };

  const handleInactivate = async (tag: AdminTag) => {
    try {
      await tagService.updateAdminTag(tag.id, { status: 'INACTIVE' });
      message.success('Etiqueta inativada');
      await loadTags();
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Nao foi possivel inativar a etiqueta');
    }
  };

  const handleReactivate = async (tag: AdminTag) => {
    try {
      await tagService.reactivateAdminTag(tag.id);
      message.success('Etiqueta reativada');
      await loadTags();
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Nao foi possivel reativar a etiqueta');
    }
  };

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, record: AdminTag) => <TagStatusBadge status={record.status} />,
    },
    {
      title: 'Atualizacao',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value: string) => new Date(value).toLocaleString('pt-BR'),
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_: unknown, record: AdminTag) => (
        <TagRowActions
          tag={record}
          onEdit={openEdit}
          onInactivate={handleInactivate}
          onReactivate={handleReactivate}
        />
      ),
    },
  ];

  return (
    <div className="admin-tags-page">
      <Space style={{ width: '100%', marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          Etiquetas
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Nova Etiqueta
        </Button>
      </Space>

      <Typography.Paragraph type="secondary">
        Gerencie etiquetas para classificacao de pesquisas e filtros da area publica/logada.
      </Typography.Paragraph>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={false}
      />

      <TagFormModal
        open={formOpen}
        loading={saving}
        initialTag={editingTag}
        onCancel={closeForm}
        onSubmit={handleSave}
      />
    </div>
  );
};

export default AdminTagsPage;
